/**
 * Typed boundary over the untyped `FetchApi` helpers for the Course Detail
 * screen. All `FormData` construction for the bookmark endpoints lives here so
 * the hook deals only with typed parameters.
 */
import ApiNames from "../constants/ApiNames";
import { apiClient } from "./NetworkService";
import type {
  ApiResponse,
  BookmarkResponse,
  CollarVideo,
  CourseDetailResponse,
} from "../types/courseDetail";
import type { QuizTopic } from "../types/misc";

const getHeaders = (token?: string) => {
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

export const getCourseTopics = async (
  courseId: number,
  cookieSubscribeId: string | null,
  token?: string
): Promise<ApiResponse<CourseDetailResponse>> => {
  const res = await apiClient.get(
    `${ApiNames.courses}${courseId}?select=topics`,
    getHeaders(token)
  );
  return res.data;
};

export const getCollarVideosDetail = async (
  courseId: number,
  cookieSubscribeId: string | null,
  token?: string
): Promise<ApiResponse<CollarVideo[]>> => {
  let endpoint = `${ApiNames.collarVideo}?course_id=${courseId}`;
  if (cookieSubscribeId) {
    endpoint += `&cookieSubscribeId=${cookieSubscribeId}`;
  }
  const res = await apiClient.get(endpoint, getHeaders(token));
  return res.data;
};

export const addVideoView = async (
  videoId: number,
  token: string | null
): Promise<unknown> => {
  const res = await apiClient.post(`${ApiNames.addView}?video_id=${videoId}`, null, getHeaders(token ?? undefined));
  return res.data;
};

export interface AddCourseBookmarkParams {
  msisdn: string | null;
  category: "Video" | "Chapter";
  key: string | number;
  title: string;
  courseName: string;
  bookmarkUrl: string;
  chapterId: number;
  courseId: number;
  videoId?: number;
  token: string | null;
}

export const addCourseBookmark = async ({
  msisdn,
  category,
  key,
  title,
  courseName,
  bookmarkUrl,
  chapterId,
  courseId,
  videoId,
  token,
}: AddCourseBookmarkParams): Promise<BookmarkResponse> => {
  const formData = new FormData();
  formData.append("msisdn", String(msisdn ?? ""));
  formData.append("category", category);
  formData.append("key", String(key));
  formData.append("title", title);
  formData.append("courseName", courseName);
  formData.append("bookmarkUrl", bookmarkUrl);
  formData.append("chapter_id", String(chapterId));
  formData.append("course_id", String(courseId));
  if (videoId) {
    formData.append("video_id", String(videoId));
  }
  const res = await apiClient.post(ApiNames.addBookMark, formData, getHeaders(token ?? undefined));
  return res.data;
};

export const removeCourseBookmark = async (params: {
  msisdn: string | null;
  bookmarkUrl: string;
  id: string | number;
  token: string | null;
}): Promise<BookmarkResponse> => {
  const formData = new FormData();
  formData.append("msisdn", String(params.msisdn ?? ""));
  formData.append("bookmarkUrl", params.bookmarkUrl);
  formData.append("id", String(params.id));
  const res = await apiClient.post(ApiNames.removeCourseBookMarks, formData, getHeaders(params.token ?? undefined));
  return res.data;
};

export const addCollarBookmark = async (params: {
  subscriberId: string | null;
  collarVideoId: number;
  collarCourseId: number;
  token: string | null;
}): Promise<BookmarkResponse> => {
  const formData = new FormData();
  formData.append("subscriber_id", String(parseInt(String(params.subscriberId), 10)));
  formData.append("collar_video_id", String(params.collarVideoId));
  formData.append("collar_course_id", String(params.collarCourseId));
  const res = await apiClient.post(ApiNames.addColorBookMark, formData, getHeaders(params.token ?? undefined));
  return res.data;
};

export const removeCollarBookmark = async (params: {
  subscriberId: string | null;
  collarVideoId: number;
  token: string | null;
}): Promise<BookmarkResponse> => {
  const formData = new FormData();
  formData.append("subscriber_id", String(params.subscriberId ?? ""));
  formData.append("collar_video_id", String(params.collarVideoId));
  const res = await apiClient.post(ApiNames.deletBookMarks, formData, getHeaders(params.token ?? undefined));
  return res.data;
};

export const getQuizTopics = async (topicId: number, token?: string): Promise<ApiResponse<QuizTopic[]>> => {
  const res = await apiClient.get(`${ApiNames.quizTopics}?topicId=${topicId}`, getHeaders(token));
  return res.data;
};
